import { useMemo, useState, useCallback } from "react";
import axios from "axios";

import PageWrapper from "../components/PageWrapper";
import PresencesList from "components/PresencesList";
import PresencesForm from "components/PresencesForm";

import useAlert from "../hooks/use-alert";
import Presence from "typings/Presence";

const cities = [
  "Camerino",
  "Ascoli Piceno",
  "Matelica",
  "San Benedetto del Tronto",
];

const PresencesPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [buildings, setBuildings] = useState<string[]>([]);
  const [rooms, setRooms] = useState<string[]>([]);
  const [presences, setPresences] = useState<Presence[]>([]);
  const { alert, alertMessage, closeAlert, showAlert } = useAlert(setIsLoading);

  const getBuildings = useCallback(
    async (city: string) => {
      try {
        const res = await axios.get<string[]>(
          `/api/rooms/buildings?city=${city}`
        );
        setBuildings(res.data);
      } catch (error) {
        showAlert("Error while fetching data. Try again.", "danger");
      }
    },
    [showAlert]
  );

  const getRooms = useCallback(
    async (city: string, building: string) => {
      try {
        const res = await axios.get<string[]>(
          `/api/rooms/names?city=${city}&building=${building}`
        );
        setRooms(res.data);
      } catch (error) {
        showAlert("Error while fetching data. Try again.", "danger");
      }
    },
    [showAlert]
  );

  const getPresences = useCallback(
    async (
      city: string,
      room: string,
      building: string,
      startDate: Date,
      endDate: Date
    ) => {
      setIsLoading(true);
      try {
        const res = await axios.post<Presence[]>(
          "/api/monitor/presences/users",
          {
            city,
            room,
            building,
            startDate,
            endDate,
          }
        );

        setPresences(res.data);
        setIsLoading(false);
        res.data.length <= 0 &&
          showAlert(
            "No presence found with the entered parameters. Try to change them.",
            "warning"
          );
      } catch (error) {
        showAlert("Error while fetching data. Try again.", "danger");
      }
    },
    [showAlert]
  );

  return (
    <PageWrapper
      isLoading={isLoading}
      loadingText="Loading presences... Hold tight"
      alert={alert}
      alertMessage={alertMessage}
      onClose={closeAlert}
    >
      <h1 className="text-center fw-bold mt-3">Search presences</h1>
      <h5 className="text-center">
        Search the presences detected within a room in a given date-time
        interval
      </h5>
      <PresencesForm
        cities={useMemo(() => cities, [])}
        buildings={buildings}
        rooms={rooms}
        getBuildings={getBuildings}
        getRooms={getRooms}
        getPresences={getPresences}
      />
      <PresencesList presences={presences} />
    </PageWrapper>
  );
};

export default PresencesPage;
